import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { CreditCard, Building2, Users, CheckCircle2, Loader2, ArrowRight, FileText, AlertCircle, XCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PaymentSetup = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [proposal, setProposal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [paymentResult, setPaymentResult] = useState(null);
  const [delegateMode, setDelegateMode] = useState(false);
  const [delegateData, setDelegateData] = useState({ name: '', email: '' });
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [pollingStatus, setPollingStatus] = useState(false);
  const [activeProvider, setActiveProvider] = useState('stripe'); // Default to stripe

  // Check if we're returning from Stripe checkout
  const sessionId = searchParams.get('session_id');
  const proposalIdFromUrl = searchParams.get('proposal_id');

  useEffect(() => {
    // Fetch active payment provider
    fetchActiveProvider();
    
    // If we have a session_id, we're returning from Stripe - poll for status
    if (sessionId) {
      pollPaymentStatus(sessionId);
    } else {
      loadProposal();
    }
  }, [token, sessionId]);

  const fetchActiveProvider = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/payments/active-provider`);
      if (response.ok) {
        const data = await response.json();
        setActiveProvider(data.active_provider || 'stripe');
      }
    } catch (err) {
      console.error('Failed to fetch active provider:', err);
      // Default to stripe on error
      setActiveProvider('stripe');
    }
  };

  const pollPaymentStatus = async (sid, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000; // 2 seconds

    setPollingStatus(true);

    if (attempts >= maxAttempts) {
      setPaymentStatus({ status: 'timeout', message: 'Payment status check timed out. Please check your email for confirmation.' });
      setPollingStatus(false);
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/payments/checkout/status/${sid}`);
      if (!response.ok) {
        throw new Error('Failed to check payment status');
      }

      const data = await response.json();
      
      if (data.payment_status === 'paid') {
        // Include the proposal_token for navigation
        setPaymentStatus({ 
          status: 'success', 
          message: 'Payment successful! Thank you for your purchase.', 
          data,
          proposalToken: data.proposal_token 
        });
        setCompleted(true);
        setPollingStatus(false);
        return;
      } else if (data.status === 'expired') {
        setPaymentStatus({ status: 'expired', message: 'Payment session expired. Please try again.' });
        setPollingStatus(false);
        return;
      }

      // If payment is still pending, continue polling
      setPaymentStatus({ status: 'processing', message: 'Payment is being processed...' });
      setTimeout(() => pollPaymentStatus(sid, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setPaymentStatus({ status: 'error', message: 'Error checking payment status. Please try again.' });
      setPollingStatus(false);
    }
  };

  const loadProposal = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/p/${token}`);
      if (response.ok) {
        const data = await response.json();
        setProposal(data);
        
        // If not signed, redirect back
        if (data.status !== 'signed') {
          setError('This proposal has not been signed yet.');
        }
      } else {
        setError('Unable to load proposal');
      }
    } catch (err) {
      setError('Failed to load proposal');
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentSetup = async () => {
    if (!selectedMethod) return;
    
    setProcessing(true);
    setError(null);
    
    try {
      // Handle delegation - send to delegate endpoint
      if (delegateMode) {
        if (!delegateData.email || !delegateData.name) {
          setError('Please enter delegate name and email');
          setProcessing(false);
          return;
        }
        
        const response = await fetch(`${BACKEND_URL}/api/payments/delegate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            proposal_id: proposal.proposal_id,
            delegate_email: delegateData.email,
            delegate_name: delegateData.name,
            payment_method: selectedMethod || 'stripe_card',
            delegator_name: proposal?.client?.main_contact_first_name 
              ? `${proposal.client.main_contact_first_name} ${proposal.client.main_contact_last_name || ''}`.trim()
              : null,
            delegator_email: proposal?.client?.main_contact_email || null,
            origin_url: window.location.origin
          })
        });

        if (response.ok) {
          const result = await response.json();
          setPaymentResult({
            ...result,
            isDelegation: true
          });
          setCompleted(true);
        } else {
          const errData = await response.json();
          setError(errData.detail || 'Failed to send payment request');
        }
        setProcessing(false);
        return;
      }
      
      // For Stripe payments, use the checkout session endpoint
      if (selectedMethod === 'stripe_card' || selectedMethod === 'stripe_bank') {
        const response = await fetch(`${BACKEND_URL}/api/payments/checkout/session`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            proposal_id: proposal.proposal_id,
            payment_method: selectedMethod,
            origin_url: window.location.origin
          })
        });

        if (response.ok) {
          const result = await response.json();
          // Redirect to Stripe checkout
          if (result.url) {
            window.location.href = result.url;
          } else {
            setError('Failed to get checkout URL');
          }
        } else {
          const errData = await response.json();
          setError(errData.detail || 'Failed to create checkout session');
        }
      } else if (selectedMethod === 'revolut_card' || selectedMethod === 'revolut_bank') {
        // For Revolut payments, use the Revolut checkout endpoint
        const response = await fetch(`${BACKEND_URL}/api/payments/revolut/checkout`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            proposal_id: proposal.proposal_id,
            payment_method: selectedMethod,
            origin_url: window.location.origin
          })
        });

        if (response.ok) {
          const result = await response.json();
          // Redirect to Revolut checkout
          if (result.url) {
            window.location.href = result.url;
          } else {
            setError('Failed to get Revolut checkout URL');
          }
        } else {
          const errData = await response.json();
          setError(errData.detail || 'Failed to create Revolut checkout session');
        }
      } else {
        // For non-Stripe methods (bank transfer, direct debit)
        const response = await fetch(`${BACKEND_URL}/api/payments/setup`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            proposal_id: proposal.proposal_id,
            payment_method: selectedMethod,
            origin_url: window.location.origin
          })
        });

        if (response.ok) {
          const result = await response.json();
          setPaymentResult(result);
          setCompleted(true);
        } else {
          const errData = await response.json();
          setError(errData.detail || 'Payment setup failed');
        }
      }
    } catch (err) {
      console.error('Payment setup error:', err);
      setError('Failed to setup payment. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const getSelectedOption = () => {
    if (!proposal?.pricing) return null;
    const pricing = proposal.pricing;
    if (pricing.selected_option_id && pricing.pricing_options) {
      return pricing.pricing_options.find(o => o.option_id === pricing.selected_option_id);
    }
    return null;
  };

  const formatCurrency = (amount) => {
    return `£${(amount || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}`;
  };

  // Show payment status if we're returning from Stripe
  if (sessionId && paymentStatus) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4">
        <div className="max-w-lg w-full bg-[#111] border border-white/10 rounded-lg p-8 text-center">
          {paymentStatus.status === 'success' && (
            <>
              <CheckCircle2 className="w-16 h-16 text-[#A2FF00] mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                Payment Successful!
              </h1>
              <p className="text-gray-400 mb-6">{paymentStatus.message}</p>
              {paymentStatus.data && (
                <div className="bg-[#A2FF00]/10 border border-[#A2FF00]/20 rounded p-4 mb-6">
                  <p className="text-[#A2FF00] text-sm mb-2">Amount Paid:</p>
                  <p className="text-2xl font-bold text-white">
                    {formatCurrency((paymentStatus.data.amount_total || 0) / 100)}
                  </p>
                </div>
              )}
            </>
          )}
          
          {paymentStatus.status === 'processing' && (
            <>
              <Loader2 className="w-16 h-16 text-[#A2FF00] mx-auto mb-4 animate-spin" />
              <h1 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                Processing Payment...
              </h1>
              <p className="text-gray-400 mb-6">{paymentStatus.message}</p>
            </>
          )}
          
          {paymentStatus.status === 'expired' && (
            <>
              <XCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                Session Expired
              </h1>
              <p className="text-gray-400 mb-6">{paymentStatus.message}</p>
            </>
          )}
          
          {paymentStatus.status === 'error' && (
            <>
              <AlertCircle className="w-16 h-16 text-yellow-400 mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                Status Check Error
              </h1>
              <p className="text-gray-400 mb-6">{paymentStatus.message}</p>
            </>
          )}
          
          {paymentStatus.status === 'timeout' && (
            <>
              <AlertCircle className="w-16 h-16 text-yellow-400 mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                Status Check Timeout
              </h1>
              <p className="text-gray-400 mb-6">{paymentStatus.message}</p>
            </>
          )}
          
          {!pollingStatus && (
            <button
              onClick={() => {
                const tokenToUse = paymentStatus?.proposalToken || token;
                if (tokenToUse) {
                  navigate(`/p/${tokenToUse}`);
                } else {
                  navigate('/');
                }
              }}
              className="px-6 py-3 bg-[#A2FF00] text-black font-bold rounded hover:opacity-90 transition-colors"
            >
              {paymentStatus.status === 'success' ? 'View Contract' : 'Try Again'}
            </button>
          )}
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#A2FF00] animate-spin" />
      </div>
    );
  }

  if (error && !proposal) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h1 className="text-2xl text-white mb-2">Oops!</h1>
          <p className="text-gray-400">{error}</p>
        </div>
      </div>
    );
  }

  if (completed) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4">
        <div className="max-w-lg w-full bg-[#111] border border-white/10 rounded-lg p-8 text-center">
          <CheckCircle2 className="w-16 h-16 text-[#A2FF00] mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Unbounded, sans-serif' }}>
            {paymentResult?.isDelegation ? 'Payment Request Sent!' : 'Payment Setup Complete!'}
          </h1>
          <p className="text-gray-400 mb-6">{paymentResult?.message}</p>
          
          {/* Delegation confirmation */}
          {paymentResult?.isDelegation && paymentResult?.delegate_email && (
            <div className="bg-blue-500/10 border border-blue-500/20 rounded p-4 mb-6 text-left">
              <h3 className="text-blue-400 font-medium mb-2">📧 Email Sent</h3>
              <p className="text-gray-300 text-sm">
                A payment request has been sent to:
              </p>
              <p className="text-white font-medium mt-1">{paymentResult.delegate_email}</p>
              <p className="text-gray-400 text-xs mt-2">
                They will receive a unique payment link to complete the payment on your behalf.
              </p>
            </div>
          )}
          
          {paymentResult?.bank_details && (
            <div className="bg-[#111] border border-white/20 rounded p-4 mb-6 text-left">
              <h3 className="text-white font-medium mb-3">Bank Transfer Details:</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-400">Account Name:</span>
                  <span className="text-white">{paymentResult.bank_details.account_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Sort Code:</span>
                  <span className="text-white font-mono">{paymentResult.bank_details.sort_code}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Account Number:</span>
                  <span className="text-white font-mono">{paymentResult.bank_details.account_number}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Reference:</span>
                  <span className="text-[#A2FF00] font-mono">{paymentResult.bank_details.reference}</span>
                </div>
              </div>
            </div>
          )}
          
          <button
            onClick={() => navigate(`/p/${token}`)}
            className="px-6 py-3 bg-[#A2FF00] text-black font-bold rounded hover:opacity-90 transition-colors"
          >
            Return to Proposal
          </button>
        </div>
      </div>
    );
  }

  const selectedOption = getSelectedOption();
  const totalAmount = selectedOption?.total_gbp || proposal?.pricing?.total_gbp || 0;
  const billingFrequency = selectedOption?.billing_frequency || proposal?.pricing?.billing_type;
  const isMonthly = billingFrequency === 'monthly';

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Header */}
      <header className="bg-[#111] border-b border-white/10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#A2FF00] rounded-sm flex items-center justify-center">
              <span className="text-black font-bold text-lg">Q</span>
            </div>
            <span className="text-white font-bold" style={{ fontFamily: 'Unbounded, sans-serif' }}>
              QSECDEF
            </span>
          </div>
          <span className="flex items-center gap-2 px-4 py-2 bg-green-500/20 text-green-400 rounded-sm">
            <CheckCircle2 className="w-5 h-5" />
            Contract Signed
          </span>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Error Display */}
        {error && (
          <div className="mb-6 p-4 bg-red-500/20 border border-red-500/30 rounded-lg flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-400" />
            <p className="text-red-400">{error}</p>
          </div>
        )}

        {/* Contract Summary */}
        <div className="bg-gradient-to-r from-[#A2FF00]/10 to-transparent border-l-4 border-[#A2FF00] p-6 mb-8">
          <h1 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Unbounded, sans-serif' }}>
            Complete Your Payment
          </h1>
          <p className="text-gray-400">
            Contract with {proposal?.client?.company_name}
          </p>
          <div className="flex items-center gap-6 mt-4">
            <div>
              <p className="text-xs text-gray-500 uppercase">Contract Value</p>
              <p className="text-2xl font-bold text-[#A2FF00]">
                {formatCurrency(totalAmount)}
                {isMonthly && <span className="text-sm text-gray-400">/month</span>}
              </p>
            </div>
            {selectedOption && (
              <div>
                <p className="text-xs text-gray-500 uppercase">Plan Selected</p>
                <p className="text-white">{selectedOption.name}</p>
              </div>
            )}
          </div>
        </div>

        {/* Payment Methods */}
        <div className="bg-[#111] border border-white/10 rounded-lg p-6 mb-6">
          <h2 className="text-xl font-bold text-white mb-6" style={{ fontFamily: 'Unbounded, sans-serif' }}>
            Choose Payment Method
          </h2>

          <div className="grid md:grid-cols-2 gap-4 mb-6">
            {/* Show payment options based on active provider */}
            {activeProvider === 'stripe' ? (
              <>
                {/* Stripe Card Payment */}
                <button
                  onClick={() => { setSelectedMethod('stripe_card'); setDelegateMode(false); }}
                  className={`p-6 rounded-lg border-2 transition-all text-left ${
                    selectedMethod === 'stripe_card'
                      ? 'border-[#A2FF00] bg-[#A2FF00]/10'
                      : 'border-white/20 hover:border-white/40'
                  }`}
                >
                  <CreditCard className={`w-8 h-8 mb-3 ${selectedMethod === 'stripe_card' ? 'text-[#A2FF00]' : 'text-gray-400'}`} />
                  <h3 className="text-white font-medium mb-1">Pay by Card</h3>
                  <p className="text-sm text-gray-400">Credit or debit card via Stripe</p>
                  {isMonthly && (
                    <p className="text-xs text-[#A2FF00] mt-2">✓ Auto-renewal enabled</p>
                  )}
                </button>

                {/* Stripe Bank Transfer */}
                <button
                  onClick={() => { setSelectedMethod('stripe_bank'); setDelegateMode(false); }}
                  className={`p-6 rounded-lg border-2 transition-all text-left ${
                    selectedMethod === 'stripe_bank'
                      ? 'border-[#A2FF00] bg-[#A2FF00]/10'
                      : 'border-white/20 hover:border-white/40'
                  }`}
                >
                  <Building2 className={`w-8 h-8 mb-3 ${selectedMethod === 'stripe_bank' ? 'text-[#A2FF00]' : 'text-gray-400'}`} />
                  <h3 className="text-white font-medium mb-1">Bank Transfer</h3>
                  <p className="text-sm text-gray-400">Pay via bank transfer (Stripe)</p>
                </button>
              </>
            ) : activeProvider === 'revolut' ? (
              <>
                {/* Revolut Card Payment */}
                <button
                  onClick={() => { setSelectedMethod('revolut_card'); setDelegateMode(false); }}
                  className={`p-6 rounded-lg border-2 transition-all text-left ${
                    selectedMethod === 'revolut_card'
                      ? 'border-[#A2FF00] bg-[#A2FF00]/10'
                      : 'border-white/20 hover:border-white/40'
                  }`}
                >
                  <CreditCard className={`w-8 h-8 mb-3 ${selectedMethod === 'revolut_card' ? 'text-[#A2FF00]' : 'text-gray-400'}`} />
                  <h3 className="text-white font-medium mb-1">Pay by Card</h3>
                  <p className="text-sm text-gray-400">Credit or debit card via Revolut</p>
                  {isMonthly && (
                    <p className="text-xs text-[#A2FF00] mt-2">✓ Auto-renewal enabled</p>
                  )}
                </button>

                {/* Revolut Bank Transfer */}
                <button
                  onClick={() => { setSelectedMethod('revolut_bank'); setDelegateMode(false); }}
                  className={`p-6 rounded-lg border-2 transition-all text-left ${
                    selectedMethod === 'revolut_bank'
                      ? 'border-[#A2FF00] bg-[#A2FF00]/10'
                      : 'border-white/20 hover:border-white/40'
                  }`}
                >
                  <Building2 className={`w-8 h-8 mb-3 ${selectedMethod === 'revolut_bank' ? 'text-[#A2FF00]' : 'text-gray-400'}`} />
                  <h3 className="text-white font-medium mb-1">Bank Transfer</h3>
                  <p className="text-sm text-gray-400">Pay via bank transfer (Revolut)</p>
                </button>
              </>
            ) : (
              <div className="col-span-2 text-center py-8 text-gray-400">
                <AlertCircle className="w-12 h-12 mx-auto mb-3 text-yellow-500" />
                <p>Payment provider not configured. Please contact support.</p>
              </div>
            )}
          </div>

          {/* Delegate Option */}
          <div className="border-t border-white/10 pt-6">
            <button
              onClick={() => { setDelegateMode(!delegateMode); setSelectedMethod(null); }}
              className={`flex items-center gap-3 p-4 rounded-lg border-2 w-full transition-all text-left ${
                delegateMode
                  ? 'border-yellow-500 bg-yellow-500/10'
                  : 'border-white/20 hover:border-white/40'
              }`}
            >
              <Users className={`w-6 h-6 ${delegateMode ? 'text-yellow-500' : 'text-gray-400'}`} />
              <div>
                <h3 className="text-white font-medium">Delegate to Someone Else</h3>
                <p className="text-sm text-gray-400">Send payment setup to another person in your organization</p>
              </div>
            </button>

            {delegateMode && (
              <div className="mt-4 p-4 bg-[#0A0A0A] rounded-lg space-y-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Delegate&apos;s Name</label>
                  <input
                    type="text"
                    value={delegateData.name}
                    onChange={(e) => setDelegateData({ ...delegateData, name: e.target.value })}
                    placeholder="e.g., John Smith"
                    className="w-full bg-[#111] border border-white/20 rounded px-3 py-2 text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Delegate&apos;s Email</label>
                  <input
                    type="email"
                    value={delegateData.email}
                    onChange={(e) => setDelegateData({ ...delegateData, email: e.target.value })}
                    placeholder="e.g., finance@company.com"
                    className="w-full bg-[#111] border border-white/20 rounded px-3 py-2 text-white"
                  />
                </div>
                <div className="mt-4">
                  <p className="text-sm text-gray-400 mb-3">Payment method for delegate:</p>
                  <div className="flex gap-2">
                    {['stripe_card', 'bank_transfer'].map((method) => (
                      <button
                        key={method}
                        onClick={() => setSelectedMethod(method)}
                        className={`px-4 py-2 rounded text-sm ${
                          selectedMethod === method
                            ? 'bg-[#A2FF00] text-black font-medium'
                            : 'bg-white/10 text-gray-400'
                        }`}
                      >
                        {method === 'stripe_card' ? 'Card Payment' : 'Invoice'}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Action Button */}
        <div className="flex justify-end">
          <button
            onClick={handlePaymentSetup}
            disabled={!selectedMethod || processing || (delegateMode && (!delegateData.name || !delegateData.email))}
            className="flex items-center gap-2 px-8 py-4 bg-[#A2FF00] text-black font-bold rounded-lg hover:opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-lg"
          >
            {processing ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                {delegateMode ? 'Send to Delegate' : 'Continue to Payment'}
                <ArrowRight className="w-5 h-5" />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaymentSetup;
